/** Seven days of uploads, or twelve months of cost, as one line.
 *
 * An accent line over an accent-900 area fill, no axes and no gridlines: the
 * figure beside it carries the value, and this only has to carry the shape.
 * The scale starts at zero unless the series never goes near it, because a
 * sparkline that crops its own baseline turns a 5% wobble into a cliff.
 *
 * The maths is exported and pure so it can be tested without a DOM, and so a
 * screen that needs the last point's position — to hang a dot off it — can
 * ask for the points rather than guess.
 */

import type { CSSProperties } from 'react';

export interface SparkGeometry {
  /** `x,y x,y …` for the line. */
  line: string;
  /** The same, closed along the baseline, for the fill. */
  area: string;
  points: Array<[number, number]>;
}

export interface SparkOptions {
  width?: number;
  height?: number;
  /** Room left above the highest point so the stroke is not clipped. */
  padding?: number;
  /** Forces the baseline to zero even when the series sits well above it. */
  zeroBased?: boolean;
}

/** Map a series into a viewBox of `width × height`, y inverted. */
export function sparkPoints(values: readonly number[], options: SparkOptions = {}): SparkGeometry {
  const width = options.width ?? 300;
  const height = options.height ?? 60;
  const padding = options.padding ?? 4;
  const usable = Math.max(1, height - padding * 2);

  const finite = values.filter((value) => Number.isFinite(value));
  const high = finite.length ? Math.max(...finite) : 0;
  const low = finite.length ? Math.min(...finite) : 0;
  // Zero-based unless the series lives far enough above zero that the shape
  // would vanish; then the floor is a tenth of the range below the minimum.
  const base = options.zeroBased === false && low > (high - low) * 2 ? low - (high - low) * 0.1 : 0;
  const span = high - base || 1;

  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points: Array<[number, number]> = values.map((value, index) => {
    const safe = Number.isFinite(value) ? value : base;
    const x = values.length > 1 ? index * step : width / 2;
    const y = padding + usable - ((safe - base) / span) * usable;
    return [Number(x.toFixed(2)), Number(y.toFixed(2))];
  });

  const line = points.map(([x, y]) => `${x},${y}`).join(' ');
  const area = points.length ? `0,${height} ${line} ${width},${height}` : '';
  return { line, area, points };
}

export interface SparklineProps extends SparkOptions {
  values: readonly number[];
  /** Words under the line — day initials, month names. */
  labels?: readonly string[];
  /** A dot on the final point, for "where it is now". */
  markLast?: boolean;
  /** What the series is, for anyone who cannot see it. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export function Sparkline({
  values,
  labels,
  markLast,
  label,
  width = 300,
  height = 60,
  padding,
  zeroBased,
  className,
  style,
}: SparklineProps) {
  const geometry = sparkPoints(values, { width, height, padding, zeroBased });
  const last = geometry.points[geometry.points.length - 1];

  return (
    <div className={className} style={style}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role={label ? 'img' : 'presentation'}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        style={{ width: '100%', height, display: 'block', overflow: 'visible' }}
      >
        <polyline points={geometry.area} fill="var(--color-accent-900)" stroke="none" />
        <polyline
          points={geometry.line}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.6"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {markLast && last ? (
          <circle cx={last[0]} cy={last[1]} r="2.5" fill="var(--color-accent-200)" />
        ) : null}
      </svg>
      {labels?.length ? (
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            color: 'var(--color-neutral-600)',
            marginTop: 4,
          }}
        >
          {labels.map((text, index) => (
            <span key={`${text}-${index}`}>{text}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Sparkline;
